<template>
  <modal-view
    @close-modal="closeModal"
    title="Subscription Details"
    customClassContent="sm:px-5 overflow-auto"
    customMaxWidth="800px"
  >
    <div class="space-y-6">
      <!-- Subscription Header -->
      <div
        class="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
      >
        <div class="flex items-center justify-between">
          <div>
            <h4
              class="text-lg font-semibold text-gray-900 dark:text-white mb-1"
            >
              {{
                (actualSubscription.plan || subscription.plan)?.display_name ||
                "Unknown Plan"
              }}
            </h4>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {{ subscriptionType }} Subscription
            </p>
          </div>
          <span
            class="px-3 py-1 text-xs font-medium rounded-full"
            :class="getStatusBadgeClass(actualStatus)"
          >
            {{ actualStatus }}
          </span>
        </div>
      </div>

      <!-- Additional Status Info -->
      <div
        v-if="
          subscription.grace_reason ||
          subscription.trial_ends_at ||
          subscription.in_trial
        "
        class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700"
      >
        <h5 class="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3">
          Additional Status Information
        </h5>
        <div class="space-y-2">
          <div v-if="subscription.grace_reason" class="flex flex-col">
            <span class="text-xs text-blue-700 dark:text-blue-300 font-medium"
              >Grace Reason:</span
            >
            <span class="text-sm text-blue-800 dark:text-blue-200">{{
              subscription.grace_reason
            }}</span>
          </div>
          <div v-if="subscription.trial_ends_at" class="flex justify-between">
            <span class="text-sm text-blue-700 dark:text-blue-300"
              >Trial Ends:</span
            >
            <span
              class="text-sm font-medium text-blue-800 dark:text-blue-200"
              >{{ formatDate(subscription.trial_ends_at) }}</span
            >
          </div>
          <div
            v-if="subscription.in_trial !== undefined"
            class="flex justify-between"
          >
            <span class="text-sm text-blue-700 dark:text-blue-300"
              >In Trial:</span
            >
            <span
              class="text-sm font-medium text-blue-800 dark:text-blue-200"
              >{{ subscription.in_trial ? "Yes" : "No" }}</span
            >
          </div>
          <div
            v-if="subscription.has_active_subscription !== undefined"
            class="flex justify-between"
          >
            <span class="text-sm text-blue-700 dark:text-blue-300"
              >Has Active Subscription:</span
            >
            <span
              class="text-sm font-medium text-blue-800 dark:text-blue-200"
              >{{ subscription.has_active_subscription ? "Yes" : "No" }}</span
            >
          </div>
        </div>
      </div>

      <!-- Subscription Details Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Left Column -->
        <div class="space-y-4">
          <div
            class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
          >
            <h5
              class="text-sm font-semibold text-gray-900 dark:text-white mb-3"
            >
              Subscription Info
            </h5>
            <div class="space-y-3">
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >ID:</span
                >
                <span
                  class="text-sm font-medium text-gray-900 dark:text-white"
                  >{{ actualSubscription.id || subscription.id }}</span
                >
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >UUID:</span
                >
                <span class="text-sm font-mono text-gray-900 dark:text-white">{{
                  actualSubscription.uuid || subscription.uuid
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Plan:</span
                >
                <span
                  class="text-sm font-medium text-gray-900 dark:text-white"
                  >{{
                    (actualSubscription.plan || subscription.plan)
                      ?.display_name || "Unknown"
                  }}</span
                >
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Status:</span
                >
                <span
                  class="text-sm font-medium"
                  :class="getStatusTextClass(actualStatus)"
                  >{{ actualStatus }}</span
                >
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Auto Renew:</span
                >
                <span
                  class="text-sm font-medium text-gray-900 dark:text-white"
                  >{{
                    actualSubscription.auto_renew || subscription.auto_renew
                      ? "Yes"
                      : "No"
                  }}</span
                >
              </div>
            </div>
          </div>

          <!-- Payment Info -->
          <div
            class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
          >
            <h5
              class="text-sm font-semibold text-gray-900 dark:text-white mb-3"
            >
              Payment Details
            </h5>
            <div class="space-y-3">
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Amount:</span
                >
                <span class="text-sm font-medium text-gray-900 dark:text-white">
                  {{
                    formatPaymentAmount(
                      subscription.payment_amount,
                      subscription.payment_currency
                    )
                  }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Currency:</span
                >
                <span
                  class="text-sm font-medium text-gray-900 dark:text-white"
                  >{{ subscription.payment_currency || "—" }}</span
                >
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Payment Method:</span
                >
                <span
                  class="text-sm font-medium text-gray-900 dark:text-white"
                  >{{ subscription.payment_method || "—" }}</span
                >
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Reference:</span
                >
                <span
                  class="text-sm font-medium text-gray-900 dark:text-white"
                  >{{ subscription.payment_reference || "—" }}</span
                >
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Paid At:</span
                >
                <span
                  class="text-sm font-medium text-gray-900 dark:text-white"
                  >{{ formatDate(subscription.paid_at) }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column -->
        <div class="space-y-4">
          <!-- Dates -->
          <div
            class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
          >
            <h5
              class="text-sm font-semibold text-gray-900 dark:text-white mb-3"
            >
              Timeline
            </h5>
            <div class="space-y-3">
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Created:</span
                >
                <span
                  class="text-sm font-medium text-gray-900 dark:text-white"
                  >{{ formatDate(subscription.created_at) }}</span
                >
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Starts:</span
                >
                <span
                  class="text-sm font-medium text-gray-900 dark:text-white"
                  >{{ formatDate(subscription.starts_at) }}</span
                >
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Expires:</span
                >
                <span
                  class="text-sm font-medium text-gray-900 dark:text-white"
                  >{{ formatDate(subscription.expires_at) }}</span
                >
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Last Updated:</span
                >
                <span
                  class="text-sm font-medium text-gray-900 dark:text-white"
                  >{{ formatDate(subscription.updated_at) }}</span
                >
              </div>
            </div>
          </div>

          <!-- Grace Period -->
          <div
            class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
          >
            <h5
              class="text-sm font-semibold text-gray-900 dark:text-white mb-3"
            >
              Grace Period
            </h5>
            <div class="space-y-3">
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Days:</span
                >
                <span
                  class="text-sm font-medium text-gray-900 dark:text-white"
                  >{{ subscription.grace_period_days || "—" }}</span
                >
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >Ends At:</span
                >
                <span
                  class="text-sm font-medium text-gray-900 dark:text-white"
                  >{{ formatDate(subscription.grace_period_ends_at) }}</span
                >
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-500 dark:text-gray-400"
                  >In Grace:</span
                >
                <span
                  class="text-sm font-medium"
                  :class="
                    subscription.is_in_grace_period
                      ? 'text-orange-600'
                      : 'text-gray-600'
                  "
                >
                  {{ subscription.is_in_grace_period ? "Yes" : "No" }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Store/Group Specific Info -->
      <div
        v-if="subscription.store_id || subscription.group_id"
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
      >
        <h5 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          {{
            subscription.store_id ? "Store Information" : "Group Information"
          }}
        </h5>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div v-if="subscription.store_id" class="space-y-3">
            <div class="flex justify-between">
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Store Name:</span
              >
              <span class="text-sm font-medium text-gray-900 dark:text-white">{{
                subscription.store_name || `Store #${subscription.store_id}`
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Store ID:</span
              >
              <span class="text-sm font-medium text-gray-900 dark:text-white">{{
                subscription.store_id
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Merchant Code:</span
              >
              <span class="text-sm font-medium text-gray-900 dark:text-white">{{
                subscription.store_merchant_code || "—"
              }}</span>
            </div>
          </div>
          <div v-if="subscription.group_id" class="space-y-3">
            <div class="flex justify-between">
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Group Name:</span
              >
              <span class="text-sm font-medium text-gray-900 dark:text-white">{{
                subscription.group_name || `Group #${subscription.group_id}`
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Group ID:</span
              >
              <span class="text-sm font-medium text-gray-900 dark:text-white">{{
                subscription.group_id
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <section v-if="actualSubscription.status_history?.length" class="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h5 class="mb-3 text-sm font-semibold">Status history</h5>
        <ol class="space-y-4">
          <li v-for="change in actualSubscription.status_history" :key="change.id" class="text-sm">
            <p class="font-medium">{{ change.previous_status }} → {{ change.status }}</p>
            <p class="text-gray-500">{{ formatDate(change.created_at) }} · Admin #{{ change.recorded_by_id }}</p>
            <p v-if="change.reason" class="mt-1 whitespace-pre-wrap">{{ change.reason }}</p>
          </li>
        </ol>
      </section>
      <section v-else-if="actualSubscription.cancellation_reason" class="rounded-lg border border-gray-200 p-4">
        <h5 class="text-sm font-semibold">Cancellation reason</h5>
        <p class="mt-2 text-sm whitespace-pre-wrap">{{ actualSubscription.cancellation_reason }}</p>
      </section>

      <!-- Plan Details -->
      <div
        v-if="subscription.plan"
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
      >
        <h5 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Plan Details
        </h5>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="space-y-3">
            <div class="flex justify-between">
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Plan Name:</span
              >
              <span class="text-sm font-medium text-gray-900 dark:text-white">{{
                subscription.plan.name
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Display Name:</span
              >
              <span class="text-sm font-medium text-gray-900 dark:text-white">{{
                subscription.plan.display_name
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Price:</span
              >
              <span class="text-sm font-medium text-gray-900 dark:text-white">
                {{
                  formatPaymentAmount(
                    subscription.plan.price_amount,
                    subscription.plan.currency
                  )
                }}
              </span>
            </div>
          </div>
          <div class="space-y-3">
            <div class="flex justify-between">
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Billing Period:</span
              >
              <span class="text-sm font-medium text-gray-900 dark:text-white"
                >{{ subscription.plan.billing_period_days }} days</span
              >
            </div>
            <div class="flex justify-between">
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Currency:</span
              >
              <span class="text-sm font-medium text-gray-900 dark:text-white">{{
                subscription.plan.currency
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Active:</span
              >
              <span
                class="text-sm font-medium"
                :class="
                  subscription.plan.is_active
                    ? 'text-green-600'
                    : 'text-red-600'
                "
              >
                {{ subscription.plan.is_active ? "Yes" : "No" }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="subscription.plan.description"
          class="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600"
        >
          <p class="text-sm text-gray-600 dark:text-gray-300">
            {{ subscription.plan.description }}
          </p>
        </div>
      </div>
    </div>
  </modal-view>
</template>

<script>
import ModalView from "./ModalView.vue";

export default {
  name: "SubscriptionDetailsModal",
  components: {
    ModalView,
  },
  props: {
    subscription: {
      type: Object,
      required: true,
    },
  },
  computed: {
    subscriptionType() {
      return this.subscription.store_id ? "Store" : "Group";
    },
    actualSubscription() {
      // Handle case where subscription data might be nested
      return this.subscription.subscription || this.subscription;
    },
    actualStatus() {
      // Handle different status field locations
      return (
        this.actualSubscription.display_status || this.subscription.display_status ||
        this.actualSubscription.status || this.subscription.status || "Unknown"
      );
    },
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    formatDate(value) {
      if (!value) {
        return "—";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatPaymentAmount(amount, currency) {
      if (!amount || amount === 0) {
        return "—";
      }

      // Amounts are stored in the main currency unit.
      const formattedAmount = Number(amount).toLocaleString();
      const currencySymbol = currency === "RWF" ? "RF" : currency;

      return `${currencySymbol} ${formattedAmount}`;
    },
    getStatusBadgeClass(status) {
      const classes = {
        ACTIVE:
          "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
        GRACE_PERIOD: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
        SCHEDULED: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
        CANCELLED: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
        EXPIRED:
          "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
        SUSPENDED:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
      };
      return classes[status] || classes.EXPIRED;
    },
    getStatusTextClass(status) {
      const classes = {
        ACTIVE: "text-green-600 dark:text-green-400",
        GRACE_PERIOD: "text-yellow-600 dark:text-yellow-400",
        SCHEDULED: "text-blue-600 dark:text-blue-400",
        CANCELLED: "text-red-600 dark:text-red-400",
        EXPIRED: "text-gray-600 dark:text-gray-400",
        SUSPENDED: "text-yellow-600 dark:text-yellow-400",
      };
      return classes[status] || classes.EXPIRED;
    },
  },
};
</script>

<style scoped>
/* Additional styling if needed */
</style>
